/**
 * <td-product-countdown> — sale countdown shown inside the Main Product buy box.
 *
 * Expects a `data-countdown-date` attribute with an ISO 8601 datetime that
 * includes a timezone offset (the Liquid wrapper appends the store's offset
 * via `'now' | date: '%z'`, so visitors in any timezone see the same end moment).
 *
 * The element fills four `.td-product-countdown__value` slots — DAYS, HOURS,
 * MINUTES, SECONDS — and removes itself when the deadline passes. Days are
 * capped at 99 so the slot never overflows two digits.
 *
 * Markup contract (set by td-product-form.liquid):
 *   <td-product-countdown data-countdown-date="...">
 *     <div class="td-product-countdown__head">...</div>
 *     <div class="td-product-countdown__timer">
 *       <div class="td-product-countdown__unit">
 *         <span class="td-product-countdown__value">--</span>
 *         <span class="td-product-countdown__label">D</span>
 *       </div>
 *       ... × 4 (D, H, M, S)
 *     </div>
 *     <div class="td-product-countdown__sub">...</div>
 *   </td-product-countdown>
 */
if (!customElements.get('td-product-countdown')) {
  class TdProductCountdown extends HTMLElement {
    constructor() {
      super();
      this.endTime = NaN;
      this.intervalId = null;
    }

    connectedCallback() {
      const raw = this.dataset.countdownDate || '';
      this.endTime = new Date(raw).getTime();

      if (!raw || Number.isNaN(this.endTime)) {
        this.remove();
        return;
      }

      this.values = this.querySelectorAll('.td-product-countdown__value');
      if (this.values.length !== 4) {
        return;
      }

      this.tick();
      this.intervalId = setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      if (this.intervalId != null) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
    }

    tick() {
      const diff = this.endTime - Date.now();
      if (diff <= 0) {
        this.remove();
        return;
      }

      const totalSeconds = Math.floor(diff / 1000);
      const days = Math.min(99, Math.floor(totalSeconds / 86400));
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      this.values[0].textContent = TdProductCountdown.pad(days);
      this.values[1].textContent = TdProductCountdown.pad(hours);
      this.values[2].textContent = TdProductCountdown.pad(minutes);
      this.values[3].textContent = TdProductCountdown.pad(seconds);
    }

    static pad(n) {
      n = n < 0 ? 0 : n;
      return n < 10 ? '0' + n : String(n);
    }
  }

  customElements.define('td-product-countdown', TdProductCountdown);
}
